import { useCallback, useEffect, useMemo, useReducer } from 'react'
import { useMediaQuery, useTheme } from '@mui/material'
import { PIN_LENGTHS, PIN_STATUSES } from 'constants/highSecurityConnection'
import useDialogs from 'hooks/useDialogs'
import useJamesApolloMutation from 'hooks/useJamesApolloMutation'
import usePinStatus from 'hooks/usePinStatus'
import { useSessionTimeout } from 'hooks/useSessionTimeout'
import useTutorials from 'hooks/useTutorials'
import { useRedirectIfPinNotSetOrBlocked, useRenderChildOrPin } from 'pages/PinLogin/hooks'
import { enterPinMutation } from 'pages/PinLogin/mutations'
import usePinKeysStorage from 'pages/PinLogin/usePinKeysStorage'
import { pinEntryInitialState, pinReducer } from 'pages/SecuritySetup/hooks'
import type { EnterPinResponseT } from 'pages/SecuritySetup/types'
import { useHistory } from 'react-router-dom'
import routes from 'services/RoutesProvider/routes'
import type { PinReducer } from './types'

export const usePinLogin = () => {
  const history = useHistory()
  const { storePinInKeychain } = usePinKeysStorage()
  const { openDialog } = useDialogs()
  const [pinStatus, statusP] = usePinStatus()
  const { isReady: isTutorialsReady } = useTutorials()
  const { breakpoints } = useTheme()
  const isSmallScreen = useMediaQuery(breakpoints.down('sm'))
  const [pinState, dispatchPin] = useReducer<PinReducer>(pinReducer, pinEntryInitialState)

  const [enterPin] = useJamesApolloMutation(enterPinMutation, {
    onCompleted: async (mutationResult: EnterPinResponseT) => {
      const enterPinRes = mutationResult?.enterPin

      if (enterPinRes.success) {
        await storePinInKeychain(pinState.pinValue)

        return
      }
      if (enterPinRes.pinBlocked) {
        dispatchPin({ type: 'pinBlocked' })

        return
      }

      dispatchPin({ type: 'pinInvalid', payload: { attemptsLeft: enterPinRes.remainRetries } })
    },
  })

  useEffect(() => {
    dispatchPin({
      type: 'setPinLength',
      payload: { pinLength: pinStatus?.pinLength || PIN_LENGTHS.default },
    })
  }, [pinStatus])

  const timeoutAction = useCallback(() => {
    openDialog('securityTimeout')
    history.push(routes.dashboard, { from: { pathname: history.location.pathname } })
  }, [history, openDialog])

  const getPinStatusData = useMemo(() => {
    return {
      pinStatusData: pinStatus,
      status: statusP,
    }
  }, [pinStatus, statusP])

  // redirect to security setup
  useRedirectIfPinNotSetOrBlocked({
    pinStatus: getPinStatusData,
    dispatchPin,
  })
  // security timeout handler
  const { sessionValidity, sessionValidityRefetch } = useSessionTimeout('securityTimeout', timeoutAction)

  // rendering logic, loading | pin | component
  const { loadingActive, componentRendered } = useRenderChildOrPin({ sessionValidity, pinStatus, pinState })

  useEffect(() => {
    if (pinState.pinLength === pinState.pinValue.length) {
      enterPin({ variables: { pinCode: pinState.pinValue } }).then(() => sessionValidityRefetch())
    }
  }, [pinState, enterPin, sessionValidityRefetch])

  const startTyping = useCallback((newVal: string) => {
    dispatchPin({ type: 'pinEntry', payload: { pinValue: newVal } })
  }, [])

  const closeSnackbar = useCallback(() => dispatchPin({ type: 'setWarning', payload: { warning: false } }), [])

  return {
    startTyping,
    loadingActive,
    componentRendered,
    history,
    pinState,
    closeSnackbar,
    displaySecureConnectionPage: getPinStatusData.pinStatusData?.pinStatus === PIN_STATUSES.notSet && isTutorialsReady,
    isSmallScreen,
  }
}
